import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { gradient } from "@/lib/format";
import {
  Leaf, ShoppingCart, LayoutDashboard, Package, Vault, Wallet, CalendarDays,
  Receipt, BarChart3, History as HistoryIcon, Gift, Globe, Users, Settings as SettingsIcon,
  Printer, LogOut, Menu, X, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/pos", label: "New Order", icon: ShoppingCart, roles: null },
  { to: "/cash-drawer", label: "Cash Drawer", icon: Wallet, roles: null },
  { to: "/customers", label: "Customers", icon: Users, roles: null },
  { to: "/daily-sales", label: "Daily Sales", icon: CalendarDays, roles: null },
  { to: "/sales", label: "Sales History", icon: Receipt, roles: null },
  { to: "/dashboard", label: "Analytics", icon: LayoutDashboard, roles: ["owner", "manager"] },
  { to: "/products", label: "Inventory", icon: Package, roles: ["owner", "manager"] },
  { to: "/safe", label: "Safe Inventory", icon: Vault, roles: ["owner", "manager"] },
  { to: "/inventory-reports", label: "Inventory Report", icon: BarChart3, roles: ["owner", "manager"] },
  { to: "/history", label: "Item History", icon: HistoryIcon, roles: ["owner", "manager"] },
  { to: "/prize-codes", label: "Prize Codes", icon: Gift, roles: ["owner", "manager"] },
  { to: "/website-sync", label: "Website Sync", icon: Globe, roles: ["owner", "manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["owner", "manager"] },
];

function PrintStatus() {
  const [status, setStatus] = useState("offline");
  useEffect(() => {
    const check = () => api.get("/print/status").then((r) => setStatus(r.data.status)).catch(() => setStatus("offline"));
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, []);
  const map = {
    online: ["bg-emerald-50 text-emerald-700 border-emerald-200", "bg-emerald-500", "Printer online"],
    polling: ["bg-amber-50 text-amber-700 border-amber-200", "bg-amber-500", "Printer polling"],
    offline: ["bg-red-50 text-red-700 border-red-200", "bg-red-500", "Printer offline"],
  };
  const [cls, dot, label] = map[status] || map.offline;
  return (
    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`} data-testid="printnode-status">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <Printer className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function HappyHourBanner() {
  const [active, setActive] = useState(false);
  useEffect(() => {
    const check = () => api.get("/pos/happy-hour").then((r) => setActive(r.data.active)).catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);
  if (!active) return null;
  return (
    <div className="bg-gradient-to-r from-purple-700 via-indigo-600 to-blue-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-semibold animate-pulse" data-testid="happy-hour-banner">
      <Zap className="w-4 h-4" /> Happy Hour is live · 4:20 – 5:20 PM · free gram + house gummie on eligible carts
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const isPOS = loc.pathname === "/pos";

  const sidebar = (
    <div className="flex flex-col h-full bg-slate-900 text-slate-300 w-64">
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-slate-800">
        <div className={`w-9 h-9 rounded-lg ${gradient} flex items-center justify-center`}>
          <Leaf className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-white font-bold leading-tight" style={{ fontFamily: "Outfit" }}>HA Grove ST</div>
          <div className="text-[11px] text-slate-500">Point of Sale</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-indigo-600 text-white" : "hover:bg-slate-800 hover:text-white"
              }`}>
            <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-2 pb-2">
          <div className="text-sm text-white font-medium">{user?.name}</div>
          <div className="text-[11px] text-slate-500 capitalize">{user?.role}</div>
        </div>
        <Button variant="ghost" onClick={logout} data-testid="logout-btn"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      <div className="hidden lg:block flex-shrink-0">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <span className="font-semibold text-slate-800" style={{ fontFamily: "Outfit" }}>
              {items.find((n) => loc.pathname.startsWith(n.to))?.label || "HA Grove ST"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <PrintStatus />
          </div>
        </header>
        <HappyHourBanner />
        <main className={`flex-1 overflow-auto ${isPOS ? "" : "p-4 md:p-6"}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
